import { createFileRoute, Link } from "@tanstack/react-router";
import { useContext } from "react";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  toast,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  AuthContext,
  AuthUtils,
  isDefined,
  logger,
} from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import {
  type PageSearch,
  pageQuery,
  validatePageSearch,
} from "@bfc-software/bfc-web-ui/lib/cursor-pagination";
import { ListPager } from "@bfc-software/bfc-web-ui/ui/shared/list-pager";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";

import { type SelectorSession } from "./-components/types";
import styles from "./index.module.css";

/**
 * `selectorId` narrows the listing to one selector_profile.id. It lives in the
 * URL rather than component state so the selector-tasks page can link straight
 * to a filtered listing, and so a filtered view stays bookmarkable.
 */
type SelectorSessionSearch = { selectorId?: string } & PageSearch;

/**
 * Rows per request.
 *
 * Held here rather than shared, because the server derives `page_number` and
 * `page_count` from the limit of the request that produced them -- so the limit
 * is part of what a page ordinal means, and a route that changes it renumbers
 * its own pages and nobody else's. Not user-adjustable: page size in the URL
 * would renumber pages under the reader mid-walk.
 */
const PAGE_SIZE = 10;

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/selector-sessions/",
)({
  validateSearch: (search?: Record<string, unknown>): SelectorSessionSearch => {
    const raw = search?.["selectorId"];
    return {
      ...(typeof raw === "string" && raw.length > 0 ? { selectorId: raw } : {}),
      ...validatePageSearch(search),
    };
  },
  staticData: {
    crumb: (_: unknown, t: TFunction) => ({
      label: t("page.selectorSessions.crumb"),
    }),
  },
  component: RouteComponent,
});

/** `ended_at == null` IS the open state (ADR-0020 §3.1.1); there is no flag. */
function isOpen(session: SelectorSession): boolean {
  return !isDefined(session.ended_at);
}

function SessionRow({
  session,
  t,
  onEnd,
  isEnding,
}: {
  session: SelectorSession;
  t: TFunction;
  onEnd: (session: SelectorSession) => void;
  isEnding: boolean;
}) {
  const { lang, org, location } = Route.useParams();
  const open = isOpen(session);

  return (
    <TableRow data-test-role="selector-session-row">
      <TableCell>
        {/*
          The selector's name IS the way in to their activations. Offered on
          every row, open or ended: the selector's activations outlive the
          session that was open when they were assigned, so an ended session is
          still a useful way in to "what is this person holding?".

          `title` because the destination is not guessable from a person's name
          alone -- the link text names who, not where.

          The AAA id rather than the row's `selector_id` ULID: the activation
          listing that page reads filters on `selectorAppUserExtIds`, and the
          service resolves an AAA id to a ULID but never the reverse -- so the id
          that has to travel in the URL is the one the filter takes. This row is
          the one place both are already in hand.
        */}
        <Link
          to="/$lang/orgs/$org/internal/whs/$location/selector-tasks/$selectorId"
          params={{ lang, org, location, selectorId: session.external_user_id }}
          title={t("page.selectorSessions.viewTasksHint")}
          data-test-role="session-selector"
        >
          {session.external_user_id}
        </Link>
      </TableCell>
      <TableCell>
        <span className={styles.sessionId} title={session.id}>
          {session.id}
        </span>
      </TableCell>
      <TableCell>
        <Tag
          label={
            open
              ? t("page.selectorSessions.status.open")
              : t("page.selectorSessions.status.ended")
          }
          display={{ tone: open ? "success" : "neutral" }}
        />
      </TableCell>
      <TableCell>
        <DateValue dateValue={session.started_at} />
      </TableCell>
      <TableCell>
        {isDefined(session.ended_at) ? (
          <DateValue dateValue={session.ended_at} />
        ) : (
          <span className={styles.emptyValue}>—</span>
        )}
      </TableCell>
      <TableCell className={styles.actionsCell}>
        <div className={styles.rowActions}>
          {/*
            Only an open session is endable. An already-ended session would get
            a 204 back (the endpoint is idempotent), but offering the action
            would read as though there were something left to close.
          */}
          {open ? (
            <Button
              kind="text-only"
              variant="secondary"
              text={t("page.selectorSessions.rowActions.end")}
              isDisabled={isEnding}
              onPress={() => onEnd(session)}
              data-test-role="session-end"
            />
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function RouteComponent() {
  const { lang, org, location } = Route.useParams();
  const { selectorId, ...page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useTaskPlannerApi();

  // eslint-disable-next-line no-restricted-properties -- TODO: migrate to useAssertedTenancyContext()
  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const tenantId = AuthUtils.getTenantId(authState, orgId, location);

  const enabled = typeof orgId === "string";

  const query = client.useQuery(
    "get",
    "/{orgId}/selector-sessions",
    {
      params: {
        path: { orgId: orgId! },
        query: {
          limit: PAGE_SIZE,
          warehouseExtId: location,
          ...(isDefined(selectorId) ? { selectorId } : {}),
          ...pageQuery(page),
        },
      },
    },
    { enabled },
  );

  const endMutation = client.useMutation(
    "post",
    "/{orgId}/selector-sessions/{sessionId}/end",
  );

  const sessions = query.data?.sessions ?? [];

  const endingId = endMutation.isPending
    ? endMutation.variables?.params?.path?.sessionId
    : undefined;

  const onEnd = (session: SelectorSession) => {
    if (!enabled || endMutation.isPending) {
      logger.info("Avoiding concurrent session end");
      return;
    }

    logger.info("Ending selector session", {
      id: session.id,
      selectorId: session.selector_id,
    });

    endMutation
      .mutateAsync({
        params: { path: { orgId: orgId!, sessionId: session.id } },
      })
      .then(() => {
        logger.info("Selector session ended", { id: session.id });
        toast.success(t("page.selectorSessions.end.success"));
        query.refetch();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to end selector session", {
          id: session.id,
          error,
        });
        const fallback = t("page.selectorSessions.end.error");
        toast.error(error instanceof Error ? error.message : fallback);
      });
  };

  return (
    <div
      className="bg-surface h-full w-full"
      data-tenant-id={tenantId}
      data-org-id={orgId}
    >
      <div className="action-bar">
        <div className="action-bar-left">
          <div className={styles.pageHeading}>
            <h1>{t("page.selectorSessions.title")}</h1>
            {/*
              The filter is only visible in the URL, so an arrival from the
              selector-tasks page would otherwise look like a warehouse with
              exactly one selector in it.
            */}
            {isDefined(selectorId) ? (
              <span
                className={styles.filterNotice}
                data-test-role="selector-filter"
              >
                {t("page.selectorSessions.filteredTo", { selectorId })}
                <Link
                  to="/$lang/orgs/$org/internal/whs/$location/selector-sessions"
                  params={{ lang, org, location }}
                  search={{}}
                  data-test-role="selector-filter-clear"
                >
                  {t("page.selectorSessions.clearFilter")}
                </Link>
              </span>
            ) : null}
          </div>

          <ListPager
            pagination={query.data?.pagination}
            isFetching={query.isFetching}
            onChange={(next) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  cursor: next.cursor,
                  dir: next.dir,
                }),
              })
            }
            t={t}
          />
        </div>
        <div className="action-bar-right">
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("page.selectorSessions.refresh")}
            isDisabled={query.isFetching}
            onPress={() => query.refetch()}
            data-test-role="selector-sessions-refresh"
          />
        </div>
      </div>
      {query.isLoading || !enabled ? (
        <Loader type="page" />
      ) : query.isError ? (
        <AppError
          description={t("page.selectorSessions.failed")}
          resolution={t("page.selectorSessions.failureSupport")}
        />
      ) : (
        <Table
          aria-label={t("page.selectorSessions.table.ariaLabel")}
          className={sessions.length < 1 ? "min-h-full" : ""}
          isResizable={false}
        >
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("page.selectorSessions.table.selector")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorSessions.table.session")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorSessions.table.status")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorSessions.table.started")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.selectorSessions.table.ended")}
            </TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableHead>
          <TableBody>
            {sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <EmptyState heading={t("page.selectorSessions.empty")} />
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  t={t}
                  onEnd={onEnd}
                  isEnding={endingId === session.id}
                />
              ))
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
